// Loading the deliverable REGISTERS the custom elements. The page carries no <script src> for
// it: the import map maps this bare specifier to the CDN, so the browser fetches one copy and
// every module shares it (conventions §12) — a bundler-using integrator writes exactly this.
import type { PayplugSession } from "@payplug/elements";
import "@payplug/elements";

// Isolation fixture: mounts the components inside a host page with hostile CSS
// (isolation.html), to prove the donut isolation (D-022/§10) holds both ways. CSS needs pixels,
// not a domain: the page feeds the components a STATIC view through a stub of the SessionClient
// port (the same gesture as the browser component tests) — no orchestrator, no backend, nothing
// to reset between runs. The view carries one settled partial tranche so the progress list has
// an item to inspect, and a card method so the form renders its inputs; the session stays
// PENDING (a full grant would reach PAID and hide both).

// The deliverable's .d.ts does not export the port types by name — an integrator never builds a
// client by hand. This fixture does, so it extracts them STRUCTURALLY from what it holds: the
// session's own property. Branded ids (attemptId) are opaque in the .d.ts, hence the one cast.
type SessionClient = NonNullable<PayplugSession["sessionClient"]>;
type SessionView = Awaited<ReturnType<SessionClient["getState"]>>;

const EUR = "EUR";
function money(amount: number) {
  return { amount, currency: EUR };
}

const VIEW: SessionView = {
  status: "PENDING",
  currency: EUR,
  orderId: "EP-123456",
  total: money(1000_00),
  remaining: money(750_00),
  expiresAt: Date.now() + 15 * 60_000,
  methods: [{ eligible: true, method: "card", schemes: [], id: "card", partialAuth: "mandated", integration: "fake" }],
  cartItems: [],
  attempts: [
    {
      attemptId: "a1" as SessionView["attempts"][number]["attemptId"],
      method: "card",
      methodId: "card",
      requested: money(250_00),
      granted: money(250_00),
      status: "AUTHORIZED",
      operation: "authorization",
      abandonable: true,
    },
  ],
};

/** A frozen stand-in for the port: one view, forever — actions resolve without doing anything
 *  (nothing in the CSS assertions dispatches one). */
const client: SessionClient = {
  getState: () => Promise.resolve(VIEW),
  subscribe: () => () => {},
  startAttempt: () => Promise.resolve({ attemptId: "frozen" } as never),
  acceptTerms: () => Promise.resolve(),
  acceptPartialAuthorization: () => Promise.resolve(),
  voidAuthorization: () => Promise.resolve(),
  abandonAttempt: () => Promise.resolve(),
  confirmAttempt: () => Promise.resolve(),
  abort: () => Promise.resolve(),
};

async function main(): Promise<void> {
  await customElements.whenDefined("payplug-session");
  const session = document.querySelector<PayplugSession>("payplug-session");
  if (session) session.sessionClient = client;
}

void main().catch((error: unknown) => {
  console.error(error);
});
